import React, { FC, ChangeEventHandler, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import {
  ReservationFlowPaymentMethodListTicketBook,
  ReservationFlowSelectedPaymentMethod,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
  ReservationFlowSelectedPaymentMethodTicketBook,
  ReservationFlowTicketBookAvailableReservationCount,
} from 'src/interfaces/reservation_flow';
import { RSBorderedListItemCollapsible } from 'src/reservation-page/bordered-list/RSBorderedListItemCollapsible';
import { RSCheckRadio } from 'src/reservation-page/RSCheckRadio';
import { RSLinkButton } from 'src/reservation-page/RSLinkButton';

import { useDisplayBehavior } from './hooks/useDisplayBehavior';

export interface TicketBookItemProps {
  value?:
    | ReservationFlowSelectedPaymentMethod
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  onChange: (
    paymentMethod: ReservationFlowSelectedPaymentMethodTicketBook,
  ) => void;
  onBlur: () => void;
  onRequireTicketBookPurchasing: (productId: number) => void;
  paymentMethodsTicketBook: ReservationFlowPaymentMethodListTicketBook;
  // YYYY-MM-DD で表される今日の日付
  today: string;
  merchantTimeZone: string;
  ticketBookAvailableReservationCounts?: ReservationFlowTicketBookAvailableReservationCount[];
  isCollapsed: boolean;
  onRequireToggling: () => void;
  selectedDate?: string; // 現時点では複数日程に対応しない
  merchantPublicId: string;
}

export const TicketBookItem: FC<TicketBookItemProps> = ({
  value,
  onChange,
  onBlur,
  onRequireTicketBookPurchasing,
  paymentMethodsTicketBook,
  today,
  merchantTimeZone,
  ticketBookAvailableReservationCounts,
  isCollapsed,
  onRequireToggling,
  selectedDate,
  merchantPublicId,
}) => {
  const intl = useIntl();

  const isSelected = useMemo(() => {
    return typeof value !== 'undefined' ? value.type === 'ticket_book' : false;
  }, [value]);

  const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      if (!event.target.checked) return;

      onChange({
        type: 'ticket_book',
        product_public_id: parseInt(event.target.value, 10),
      });
      onBlur();
    },
    [onChange, onBlur],
  );

  const { items } = useDisplayBehavior({
    value,
    today,
    merchantTimeZone,
    paymentMethodsTicketBook,
    ticketBookAvailableReservationCounts,
    selectedDate,
    merchantPublicId,
  });

  return (
    <RSBorderedListItemCollapsible
      leftContent={
        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
          {intl.formatMessage({
            defaultMessage: '回数券',
            id: 'gOR5Wo',
            description:
              'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/index.tsx',
          })}
        </div>
      }
      details={
        <div className="px-2.5 py-3">
          <ul className="list-style-none m-0 space-y-2.5 p-0">
            {items.map(
              ({
                product,
                isSelected,
                isDisabled,
                isPurchasable,
                availableTicketBooks,
                notes,
              }) => {
                return (
                  <li
                    key={`paymentMethod_ticketBook_products_${product.public_id}`}
                    className="block"
                  >
                    <div className="flex items-center justify-between">
                      <label className="flex flex-auto cursor-pointer items-center justify-between">
                        <div className="flex flex-auto items-center justify-between">
                          <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                            {product.name}
                          </div>
                          <div className="rs-font text-13 text-gray-1000 pc:text-12 font-normal">{`${intl.formatMessage(
                            {
                              defaultMessage: '消費回数：',
                              id: 'cgp8x0',
                              description:
                                'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/index.tsx',
                            },
                          )}${product.consumption}`}</div>
                        </div>

                        {(() => {
                          if (isPurchasable) {
                            return (
                              <div className="rs-font text-16 pc:text-15 ml-2 flex-none font-normal">
                                <RSLinkButton
                                  data-rsv-m3-ticket-book={`purchase-button`}
                                  type="button"
                                  onClick={() =>
                                    onRequireTicketBookPurchasing(
                                      product.public_id,
                                    )
                                  }
                                >
                                  {intl.formatMessage({
                                    defaultMessage: '購入',
                                    id: 'lK0Xo8',
                                    description:
                                      'src/templates/merchants/resources/book/components/payment-method/TicketBookItem/index.tsx',
                                  })}
                                </RSLinkButton>
                              </div>
                            );
                          }

                          return (
                            <div className="ml-1.5 flex-none">
                              <RSCheckRadio
                                name="paymentMethod"
                                value={product.public_id.toString()}
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={onInputChange}
                              />
                            </div>
                          );
                        })()}
                      </label>
                    </div>

                    <div className="mt-0.5 space-y-[2px]">
                      {availableTicketBooks.map(({ publicId, notes }) => {
                        return (
                          <ul
                            key={`paymentMethod_ticketBook_products_available_${publicId}`}
                            className="rs-font text-13 text-gray-1000 pc:text-12 m-0 list-none p-0 font-normal"
                          >
                            {notes.map(({ text, isWarning }, index) => {
                              return (
                                <li
                                  key={`paymentMethod_ticketBook_products_available_${publicId}_detail_${index}`}
                                  className={classnames(
                                    'inline',
                                    '[&:not(:first-child):before]:content-["/"]',
                                    '[&:not(:first-child):before]:text-gray-200',
                                    '[&:not(:first-child):before]:mx-[0.25em]',
                                    isWarning
                                      ? 'text-red-700'
                                      : 'text-gray-1000',
                                  )}
                                >
                                  {text}
                                </li>
                              );
                            })}
                          </ul>
                        );
                      })}
                    </div>

                    {(() => {
                      if (typeof notes === 'undefined' || notes.length === 0) {
                        return null;
                      }

                      return (
                        <div className="mt-[2px] space-y-[2px]">
                          {notes.map(({ text, isWarning }, index) => {
                            return (
                              <div
                                key={`paymentMethod_ticketBook_products_${product.public_id}_text_${index}`}
                                className={classnames(
                                  'rs-font',
                                  'text-13',
                                  'pc:text-12',
                                  'font-normal',
                                  !isWarning
                                    ? 'text-gray-1000'
                                    : 'text-red-700',
                                )}
                              >
                                {text}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </li>
                );
              },
            )}
          </ul>
        </div>
      }
      isCollapsed={isCollapsed}
      isSelected={isSelected}
      onClick={onRequireToggling}
    />
  );
};
